// 使用者與權限 → 分享（SPEC #org-model-shares、#vms-web-801、#partners-302；取代「外部服務 / 服務關係」）：
// 家戶 owner / admin 看、新增、縮小、撤銷這個家戶分享給個人與組織的分享，同意組織的分享請求（代碼 / 連結），一鍵續約，看分享存取紀錄。
//   - 分享給個人：輸入對方 email（同一個帳號池）；有帳號立即生效、沒有寄邀請；兩種都只顯示「已送出」（不洩漏帳號存在與否）。
//     可分享的只有資料動作，預設只勾「看」；可設到期日、可勾往前追溯（最多 30 天）。
//   - 分享給組織：組織出示分享請求代碼，這裡看內容（審核徽章、用途、範圍、條件、期限）後同意；只能 ≤ 請求範圍。
//   - 分享只能縮不能擴；收到分享的人 / 組織不能再分享。含影像 / 布撤防 / 施工模式時伺服器要 5 分鐘內重新驗證（SDK 自動跳對話框）。
import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getHomeOf } from '@kinio/web-sdk';
import { useConfirm } from '../../components/ConfirmDialog';
import { useHomeLabel, useScope } from '../../lib/scope';
import { aidTail, modelLabel, refFullName } from '../../lib/devices';
import {
  MEDIA_ACTIONS,
  parseShareCode,
  PERSONAL_SHARE_ACTIONS,
  PERSONAL_SHARE_DEFAULT,
  shareApi,
  type HomeShare,
  type ScopeHome,
  type ShareConditions,
  type ShareRequestPreview,
  type ShareTarget,
} from '../../lib/planeApi';
import { Dialog } from '../../components/Dialog';
import { errText } from '../settings/OrgSection';
import { dateStamp, stamp } from '../../lib/timefmt';

type T = (k: string, o?: Record<string, unknown>) => string;

/** 能管理分享的家戶：我是家戶 owner / admin（分享是管理動作，SPEC #device-permissions-layers）。 */
export function sharingHomes(homes: ScopeHome[]): ScopeHome[] {
  return homes.filter((h) => !h.external && (h.can_manage || h.role === 'owner' || h.role === 'admin'));
}

export function scopeLabel(t: T, s: string): string {
  return t(`actions.${s}`, { defaultValue: s });
}

export function condLabel(t: T, c: ShareConditions | undefined): string {
  if (!c) return '';
  if (c.media_condition === 'alarm_window') return t('ext.cond_alarm_window', { n: c.window_minutes ?? 30 });
  return t(`ext.cond_${c.media_condition}`, { defaultValue: c.media_condition });
}

/** 分享對象的顯示：個人 = 名字（已註冊的，平台 H 起才有）或 email（等對方註冊的只有 email）；組織 = 名稱 + 審核徽章。 */
export function granteeLabel(t: T, s: Pick<HomeShare, 'grantee'>): string {
  if (s.grantee.kind === 'user') return s.grantee.display_name || s.grantee.email || t('shr.someone');
  return s.grantee.display_name || t('shr.an_org');
}

export function SharesTab() {
  const { t } = useTranslation();
  const { homes } = useScope();
  const homeLabel = useHomeLabel();
  const mine = sharingHomes(homes);
  const [homeId, setHomeId] = useState('');
  const home = mine.find((h) => h.home_id === homeId) ?? mine[0];
  if (!home) return <p className="card muted">{t('shr.no_homes')}</p>;
  return (
    <>
      {mine.length > 1 && (
        <div className="toolbar">
          <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <span>{t('people.home')}</span>
            <select className="input" style={{ width: 'auto' }} value={home.home_id} onChange={(e) => setHomeId(e.target.value)}>
              {mine.map((h) => (
                <option key={h.home_id} value={h.home_id}>
                  {homeLabel(h)}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      <HomeShares home={home} />
      <AccessLog home={home} />
    </>
  );
}

function HomeShares({ home }: { home: ScopeHome }) {
  const { t } = useTranslation();
  const { ask, dialog } = useConfirm();
  const qc = useQueryClient();
  const api = home.endpoints.api;
  const q = useQuery({ queryKey: ['home-shares', home.home_id], queryFn: () => shareApi.listWithManage(api, home.home_id) });
  // 能不能分享 / 縮小 / 撤銷：清單回的 can_manage（平台 H 起）；舊後端沒回就照家戶角色（這頁只列 owner / admin 的家戶）
  const canManage = q.data?.can_manage ?? true;
  const [adding, setAdding] = useState<'person' | 'org' | null>(null);
  const [shrinking, setShrinking] = useState<HomeShare | null>(null);
  const [renewing, setRenewing] = useState<HomeShare | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['home-shares', home.home_id] });
  const revoke = useMutation({ mutationFn: (id: string) => shareApi.revoke(api, home.home_id, id), onSuccess: refresh, onError: (e) => setErr(errText(t, e)) });
  const active = (q.data?.shares ?? []).filter((s) => s.status !== 'ended');
  const people = active.filter((s) => s.grantee.kind === 'user');
  const orgs = active.filter((s) => s.grantee.kind === 'org');
  const row = (s: HomeShare) => (
    <li key={s.share_id} className="rel-card" data-share={s.share_id}>
      <div className="row">
        <strong>{granteeLabel(t, s)}</strong>
        {s.grantee.kind === 'user' && s.grantee.display_name && s.grantee.email && s.grantee.display_name !== s.grantee.email && (
          <span className="small muted">{s.grantee.email}</span>
        )}
        {(s.grantee.verified_kinds ?? []).map((k) => (
          <span key={k} className="badge badge-ok">
            {t('ext.verified', { kind: t(`ext.kind_${k}`, { defaultValue: k }) })}
          </span>
        ))}
        {s.grantee.kind === 'org' && <span className="chip">{t(`ext.purpose_${s.purpose}`, { defaultValue: s.purpose })}</span>}
        {s.status === 'suspended' && <span className="badge badge-warn">{t('svc.status_suspended')}</span>}
        <div className="spacer" />
        <span className="small muted">
          {s.expires_at ? t('ext.until', { date: dateStamp(s.expires_at) }) : t('shr.no_expiry')}
        </span>
        {canManage && s.grantee.kind === 'org' && (
          <button className="btn btn-sm" onClick={() => setRenewing(s)}>
            {t('shr.renew')}
          </button>
        )}
        {canManage && (
          <button className="btn btn-sm" onClick={() => setShrinking(s)}>
            {t('ext.shrink')}
          </button>
        )}
        {canManage && (
          <button
            className="btn btn-sm"
            disabled={revoke.isPending}
            onClick={() => {
              void ask({ danger: true, title: t('shr.revoke_confirm', { name: granteeLabel(t, s) }), action: t('common.action_revoke') }).then((ok) => ok && revoke.mutate(s.share_id));
            }}
          >
            {t('ext.revoke')}
          </button>
        )}
      </div>
      <div className="small">{s.scopes.map((x) => scopeLabel(t, x)).join(t('common.list_sep'))}</div>
      <div className="small muted">
        {s.grantee.kind === 'org' && condLabel(t, s.conditions) + ' · '}
        {t('people.targets', { n: s.targets.filter((x) => !x.historical).length })}
        {s.auto_include_new && ' · ' + t('ext.auto_include_new_on')}
      </div>
    </li>
  );
  return (
    <section className="card">
      <div className="toolbar">
        <h2 className="section-title">{t('shr.title', { name: home.display_name || home.home_id })}</h2>
        <div className="spacer" />
        {canManage && (
          <>
            <button className="btn btn-primary btn-sm" onClick={() => setAdding('person')} data-testid="share-person">
              {t('shr.to_person')}
            </button>
            <button className="btn btn-sm" onClick={() => setAdding('org')} data-testid="share-org">
              {t('shr.to_org')}
            </button>
          </>
        )}
      </div>
      <p className="muted small">{t('shr.hint')}</p>
      {err && <p className="error">{err}</p>}
      {q.isLoading && <p className="muted">{t('common.loading')}</p>}
      {q.isError && <p className="error">{errText(t, q.error)}</p>}
      {q.data && active.length === 0 && <p className="muted">{t('shr.none')}</p>}
      {people.length > 0 && (
        <>
          <h3 className="small">{t('shr.people')}</h3>
          <ul className="plain-list">{people.map(row)}</ul>
        </>
      )}
      {orgs.length > 0 && (
        <>
          <h3 className="small">{t('shr.orgs')}</h3>
          <ul className="plain-list">{orgs.map(row)}</ul>
        </>
      )}
      <PersonShareDialog home={home} open={adding === 'person'} close={() => setAdding(null)} done={refresh} />
      <AcceptOrgDialog home={home} open={adding === 'org'} close={() => setAdding(null)} done={refresh} />
      <ShrinkDialog home={home} share={shrinking} close={() => setShrinking(null)} done={refresh} />
      <RenewDialog home={home} share={renewing} close={() => setRenewing(null)} done={refresh} />
      {dialog}
    </section>
  );
}

/**
 * 能不能分享（SPEC #org-model-shares「可以分享的設備種類」，Eddy 2026-10-14 選單）：照機種——`kinio-node` 開頭（含 kinio-node-host）
 * 是 Kinio Node、`kinio-ap` 開頭是 Wi-Fi 基地台，都不能分享；通道與子設備不另判斷，查不到機種的不擋（平台同一套，帶到回 422 SHARE_KIND_NOT_ALLOWED）。
 */
export function shareableModel(model: string | undefined | null): boolean {
  const m = (model ?? '').toLowerCase();
  return !(m.startsWith('kinio-node') || m.startsWith('kinio-ap'));
}

/** 家戶的設備（分享的目標；只列本體與通道的 device_ref）。shareable = 能不能當分享的目標（清單名稱查表照樣用全部）。 */
export function useHomeDevices(home: ScopeHome, enabled: boolean) {
  const things = useQuery({ queryKey: ['home', home.home_id], queryFn: () => getHomeOf(home), enabled });
  const { devices } = useScope();
  return useMemo(() => {
    // 沒取名的設備用全站同一個名字（第八輪 B8-04：RDBT 顯示成原始 ID「CAM-…」）：使用者取的 → 「我的設備」平台名 → 機種＋末 4 碼
    const me = new Map<string, string>();
    for (const d of devices) {
      if (d.home_id !== home.home_id) continue;
      if (d.display_name) me.set(d.device_ref, d.display_name);
      for (const c of d.children ?? []) if (c.display_name) me.set(c.device_ref, c.display_name);
    }
    const all = (things.data?.things ?? []).map((x) => {
      const ref = String(x.device_ref ?? '');
      const model = (x as { thing?: { thing?: { model?: string } } }).thing?.thing?.model;
      const own = x.display_name ? String(x.display_name) : me.get(ref) || (model && !ref.includes(':ch') ? `${modelLabel(model)} ${aidTail(ref)}` : undefined);
      return { ref, own, model };
    });
    const named = all.map((x) => ({ device_ref: x.ref, display_name: x.own }));
    return all.map((x) => ({
      ref: x.ref,
      // 通道寫完整名「NVR631 / CH1」（B-23）
      name: refFullName(x.ref, x.own, named),
      shareable: x.ref.includes(':ch') || shareableModel(x.model),
    }));
  }, [things.data, devices, home.home_id]);
}

/** 分享的設備清單：不列 Kinio Node 與 Wi-Fi 基地台（不能分享）。 */
export function DevicePicker({ list, picked, setPicked, all }: { list: { ref: string; name: string; shareable?: boolean }[]; picked: string[]; setPicked(v: string[]): void; all?: boolean }) {
  return (
    <div className="check-grid">
      {list.filter((d) => all || d.shareable !== false).map((d) => (
        <label key={d.ref} className="check">
          <input type="checkbox" checked={picked.includes(d.ref)} onChange={(e) => setPicked(e.target.checked ? [...picked, d.ref] : picked.filter((x) => x !== d.ref))} />
          <span>{d.name}</span>
        </label>
      ))}
    </div>
  );
}

function PersonShareDialog({ home, open, close, done }: { home: ScopeHome; open: boolean; close(): void; done(): void }) {
  const { t } = useTranslation();
  const devices = useHomeDevices(home, open);
  const [email, setEmail] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [scopes, setScopes] = useState<string[]>(PERSONAL_SHARE_DEFAULT);
  const [expires, setExpires] = useState('');
  const [history, setHistory] = useState(0);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  function reset() {
    setEmail('');
    setPicked([]);
    setScopes(PERSONAL_SHARE_DEFAULT);
    setExpires('');
    setHistory(0);
    setSent(false);
    setErr(null);
    close();
  }
  const save = useMutation({
    mutationFn: () =>
      shareApi.toPerson(home.endpoints.api, home.home_id, {
        email: email.trim(),
        targets: picked.map((ref): ShareTarget => ({ device_ref: ref })),
        scopes,
        expires_at: expires ? new Date(expires + 'T23:59:59').toISOString() : undefined,
        history_from: history ? new Date(Date.now() - history * 86400_000).toISOString() : undefined,
      }),
    onSuccess: () => {
      setSent(true);
      done();
    },
    onError: (e) => setErr(errText(t, e)),
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    save.mutate();
  }
  return (
    <Dialog open={open} title={t('shr.to_person')} onClose={reset} wide>
      {sent ? (
        <div className="form">
          <p className="notice" data-testid="share-sent">
            {t('shr.sent', { email })}
          </p>
          <button className="btn btn-primary" onClick={reset}>
            {t('common.done')}
          </button>
        </div>
      ) : (
        <form className="form" onSubmit={submit}>
          <p className="muted small">{t('shr.person_hint')}</p>
          <label className="field">
            <span>{t('auth.email')}</span>
            <input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
          </label>
          <fieldset className="field">
            <legend>{t('ext.devices')}</legend>
            <DevicePicker list={devices} picked={picked} setPicked={setPicked} />
          </fieldset>
          <fieldset className="field">
            <legend>{t('ext.scopes')}</legend>
            <div className="check-grid">
              {PERSONAL_SHARE_ACTIONS.map((s) => (
                <label key={s} className="check">
                  <input
                    type="checkbox"
                    checked={scopes.includes(s)}
                    disabled={s === 'device.read'}
                    onChange={(e) => setScopes(e.target.checked ? [...scopes, s] : scopes.filter((x) => x !== s))}
                  />
                  <span>{scopeLabel(t, s)}</span>
                </label>
              ))}
            </div>
            <span className="small muted">{t('shr.no_reshare')}</span>
          </fieldset>
          <label className="field">
            <span>{t('shr.expires')}</span>
            <input className="input" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} style={{ width: 'auto' }} />
            <span className="small muted">{t('shr.expires_hint')}</span>
          </label>
          <label className="field">
            <span>{t('ext.history')}</span>
            <select className="input" value={history} onChange={(e) => setHistory(Number(e.target.value))}>
              <option value={0}>{t('ext.history_none')}</option>
              <option value={7}>{t('ext.history_days', { count: 7 })}</option>
              <option value={30}>{t('ext.history_days', { count: 30 })}</option>
            </select>
          </label>
          {scopes.some((s) => MEDIA_ACTIONS.includes(s)) && <p className="small muted">{t('ext.consent_note')}</p>}
          {err && <div className="error">{err}</div>}
          <div className="row-end">
            <button type="button" className="btn" onClick={reset}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" disabled={save.isPending || picked.length === 0 || scopes.length === 0}>
              {t('shr.send')}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

export function AcceptOrgDialog({ home, open, close, done }: { home: ScopeHome; open: boolean; close(): void; done(): void }) {
  const { t, i18n } = useTranslation();
  const api = home.endpoints.api;
  const devices = useHomeDevices(home, open);
  const [code, setCode] = useState('');
  const [pv, setPv] = useState<ShareRequestPreview | null>(null);
  const [scopes, setScopes] = useState<string[]>([]);
  const [mode, setMode] = useState<'all' | 'list'>('all');
  const [picked, setPicked] = useState<string[]>([]);
  const [media, setMedia] = useState<ShareConditions['media_condition']>('alarm_window');
  const [auto, setAuto] = useState(false);
  const [history, setHistory] = useState(0);
  const [lan, setLan] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  function reset() {
    setCode('');
    setPv(null);
    setErr(null);
    close();
  }
  // 貼上 deep link（…/r/<code>?env=<plane>）也收；連結指到別的 plane → 這個家戶不能同意（分享在家戶所在的 plane）
  const parsed = parseShareCode(code);
  const wrongEnv = !!parsed.env && parsed.env !== home.env;
  const preview = useMutation({
    mutationFn: () => shareApi.preview(api, parsed.code),
    onSuccess: (r) => {
      setPv(r);
      setScopes(r.request.scopes);
      setMedia(r.request.conditions.media_condition);
      // 請求只要某幾台（list）→ 預設只勾那幾台
      const listed = r.request.resource_scope?.mode === 'list' ? (r.request.resource_scope.devices ?? []).map((d) => d.device_ref) : [];
      if (listed.length) {
        setMode('list');
        setPicked(listed);
      }
      setErr(null);
    },
    onError: (e) => setErr(errText(t, e)),
  });
  const accept = useMutation({
    mutationFn: () =>
      shareApi.acceptOrg(api, home.home_id, {
        code: parsed.code,
        scopes,
        // 「全部設備」= 家裡能分享的全部（Kinio Node、Wi-Fi 基地台不能分享，帶到平台會整筆 422 SHARE_KIND_NOT_ALLOWED；SPEC #org-model-shares）
        targets: (mode === 'all' ? devices.filter((d) => d.shareable).map((d) => d.ref) : picked).map((ref) => ({ device_ref: ref })),
        conditions: { ...pv!.request.conditions, media_condition: media },
        auto_include_new: auto,
        history_from: history ? new Date(Date.now() - history * 86400_000).toISOString() : undefined,
        install_lan_mode: lan,
        locale: i18n.language,
      }),
    onSuccess: () => {
      done();
      reset();
    },
    onError: (e) => setErr(errText(t, e)),
  });
  const req = pv?.request;
  return (
    <Dialog open={open} title={t('shr.to_org')} onClose={reset} wide>
      {!pv || !req ? (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            preview.mutate();
          }}
        >
          <p className="muted small">{t('ext.code_hint')}</p>
          <label className="field">
            <span>{t('ext.code')}</span>
            <input className="input mono" required value={code} onChange={(e) => setCode(e.target.value)} autoFocus />
          </label>
          {wrongEnv && <div className="error">{t('shr.wrong_env', { env: parsed.env })}</div>}
          {err && <div className="error">{err}</div>}
          <button className="btn btn-primary" disabled={preview.isPending || wrongEnv}>
            {t('ext.preview')}
          </button>
        </form>
      ) : (
        <div className="form">
          <div className="row">
            <strong>{pv.org.display_name}</strong>
            {(pv.org.verified_kinds ?? []).map((k) => (
              <span key={k} className="badge badge-ok">
                {t('ext.verified', { kind: t(`ext.kind_${k}`, { defaultValue: k }) })}
              </span>
            ))}
            {!pv.verified && <span className="badge badge-warn">{t('shr.unverified')}</span>}
            <span className="chip">{t(`ext.purpose_${req.purpose}`, { defaultValue: req.purpose })}</span>
            <span className="muted small">{t('ext.duration', { count: req.duration_days })}</span>
          </div>
          {req.kind === 'renewal' && <p className="notice small">{t('shr.is_renewal')}</p>}
          {req.note && <p className="notice small">{req.note}</p>}
          {req.resource_scope?.mode === 'list' && (req.resource_scope.devices ?? []).length > 0 && (
            <p className="small" data-testid="requested-devices">
              {t('shr.requested_devices')}
              {t('common.colon')}
              {(req.resource_scope.devices ?? []).map((d) => d.name || d.device_ref).join(t('common.list_sep'))}
            </p>
          )}
          <fieldset className="field">
            <legend>{t('ext.scopes')}</legend>
            <div className="check-grid">
              {req.scopes.map((s) => (
                <label key={s} className="check">
                  <input type="checkbox" checked={scopes.includes(s)} onChange={(e) => setScopes(e.target.checked ? [...scopes, s] : scopes.filter((x) => x !== s))} />
                  <span>{scopeLabel(t, s)}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset className="field">
            <legend>{t('ext.devices')}</legend>
            <label className="check">
              <input type="radio" checked={mode === 'all'} onChange={() => setMode('all')} />
              <span>{t('ext.devices_all')}</span>
            </label>
            <label className="check">
              <input type="radio" checked={mode === 'list'} onChange={() => setMode('list')} />
              <span>{t('ext.devices_list')}</span>
            </label>
            {mode === 'list' && <DevicePicker list={devices} picked={picked} setPicked={setPicked} />}
            <label className="check">
              <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} />
              <span>{t('ext.auto_include_new')}</span>
            </label>
          </fieldset>
          {scopes.some((s) => MEDIA_ACTIONS.includes(s)) && (
            <label className="field">
              <span>{t('ext.media_condition')}</span>
              <select className="input" value={media} onChange={(e) => setMedia(e.target.value as ShareConditions['media_condition'])}>
                <option value="alarm_window">{t('ext.cond_alarm_window', { n: req.conditions.window_minutes ?? 30 })}</option>
                <option value="armed">{t('ext.cond_armed')}</option>
                <option value="always">{t('ext.cond_always')}</option>
              </select>
            </label>
          )}
          <label className="field">
            <span>{t('ext.history')}</span>
            <select className="input" value={history} onChange={(e) => setHistory(Number(e.target.value))}>
              <option value={0}>{t('ext.history_none')}</option>
              <option value={7}>{t('ext.history_days', { count: 7 })}</option>
              <option value={30}>{t('ext.history_days', { count: 30 })}</option>
            </select>
          </label>
          {req.purpose === 'install' && (
            <label className="check">
              <input type="checkbox" checked={lan} onChange={(e) => setLan(e.target.checked)} />
              <span>{t('ext.install_lan')}</span>
            </label>
          )}
          <p className="small muted">{t('ext.consent_note')}</p>
          {err && <div className="error">{err}</div>}
          <div className="row-end">
            <button className="btn" onClick={reset}>
              {t('common.cancel')}
            </button>
            <button
              className="btn btn-primary"
              disabled={accept.isPending || scopes.length === 0 || (mode === 'list' ? picked.length === 0 : !devices.some((d) => d.shareable))}
              onClick={() => accept.mutate()}
            >
              {t('ext.accept')}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function ShrinkDialog({ home, share, close, done }: { home: ScopeHome; share: HomeShare | null; close(): void; done(): void }) {
  const { t } = useTranslation();
  const [scopes, setScopes] = useState<string[]>([]);
  const [drop, setDrop] = useState<string[]>([]);
  const devNames = useHomeDevices(home, !!share);
  const devName = (ref: string) => devNames.find((d) => d.ref === ref)?.name ?? t('ext.device_unknown');
  const [forId, setForId] = useState('');
  const [err, setErr] = useState<string | null>(null);
  if (share && share.share_id !== forId) {
    setForId(share.share_id);
    setScopes(share.scopes);
    setDrop([]);
    setErr(null);
  }
  const save = useMutation({
    mutationFn: () => shareApi.shrink(home.endpoints.api, home.home_id, share!, { scopes, drop_devices: drop.length ? drop : undefined }),
    onSuccess: () => {
      done();
      close();
    },
    onError: (e) => setErr(errText(t, e)),
  });
  return (
    <Dialog open={!!share} title={t('ext.shrink_of', { name: share ? granteeLabel(t, share) : '' })} onClose={close}>
      {share && (
        <div className="form">
          <p className="muted small">{t('ext.shrink_hint')}</p>
          <fieldset className="field">
            <legend>{t('ext.scopes')}</legend>
            {share.scopes.map((s) => (
              <label key={s} className="check">
                <input type="checkbox" checked={scopes.includes(s)} onChange={(e) => setScopes(e.target.checked ? [...scopes, s] : scopes.filter((x) => x !== s))} />
                <span>{scopeLabel(t, s)}</span>
              </label>
            ))}
          </fieldset>
          <fieldset className="field">
            <legend>{t('ext.devices')}</legend>
            {share.targets
              .filter((x) => !x.historical)
              .map((x) => (
                <label key={x.device_ref} className="check">
                  <input type="checkbox" checked={!drop.includes(x.device_ref)} onChange={(e) => setDrop(e.target.checked ? drop.filter((d) => d !== x.device_ref) : [...drop, x.device_ref])} />
                  <span>{devName(x.device_ref)}</span>
                </label>
              ))}
          </fieldset>
          {err && <div className="error">{err}</div>}
          <div className="row-end">
            <button className="btn" onClick={close}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" disabled={save.isPending || scopes.length === 0} onClick={() => save.mutate()}>
              {t('common.save')}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

/** 一鍵續約（monitoring / security_service）：組織送的續約請求代碼，範圍不變、新版本與新同意。 */
function RenewDialog({ home, share, close, done }: { home: ScopeHome; share: HomeShare | null; close(): void; done(): void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => shareApi.renew(home.endpoints.api, home.home_id, share!.share_id, code.trim()),
    onSuccess: () => {
      setCode('');
      done();
      close();
    },
    onError: (e) => setErr(errText(t, e)),
  });
  return (
    <Dialog open={!!share} title={t('shr.renew_of', { name: share ? granteeLabel(t, share) : '' })} onClose={close}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          save.mutate();
        }}
      >
        <p className="muted small">{t('shr.renew_hint')}</p>
        <label className="field">
          <span>{t('ext.code')}</span>
          <input className="input mono" required value={code} onChange={(e) => setCode(e.target.value)} autoFocus />
        </label>
        {err && <div className="error">{err}</div>}
        <div className="row-end">
          <button type="button" className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" disabled={save.isPending}>
            {t('shr.renew')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function AccessLog({ home }: { home: ScopeHome }) {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ['share-access', home.home_id], queryFn: () => shareApi.accessLog(home.endpoints.api, home.home_id) });
  const rows = q.data?.entries ?? [];
  // 設備欄顯示完整名（「NVR631 / CH5」），不顯示 device_ref（SPEC #vms-web-801、P0-7）
  const devs = useHomeDevices(home, rows.length > 0);
  const devName = (ref: string) => devs.find((d) => d.ref === ref)?.name || t('shr.unknown_device');
  return (
    <section className="card">
      <h2 className="section-title">{t('shr.access_log')}</h2>
      <p className="muted small">{t('shr.access_log_hint')}</p>
      {rows.length === 0 && !q.isLoading && <p className="muted small">{t('ext.access_log_empty')}</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>{t('ext.when')}</th>
                <th>{t('shr.grantee')}</th>
                <th>{t('ext.who')}</th>
                <th>{t('ext.what')}</th>
                <th>{t('ext.device')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.access_id}>
                  <td className="small muted">{stamp(a.created_at)}</td>
                  <td className="small">{t(`shr.grantee_${a.grantee_kind}`)}</td>
                  <td className="small">{a.actor_name || t('shr.someone')}</td>
                  <td className="small">{scopeLabel(t, a.action)}</td>
                  <td className="small">{a.device_ref ? devName(a.device_ref) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
